import React, { useState, useRef, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import CalculatorLayout from "../components/CalcLayout";
import Input from "../components/Input";
import { Button } from "../components/Button";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  setLoanAmmount,
  setTimePeriod,
  setIntrestRate,
  setNewEmi,
  setPrePayment,
  setTimeUnit,
  resetEarlyData,
  setTotalAmount,
  setRemainingPrinciple,
} from "../redux/calculations/earlyLoanSlice";
import {
  resetChartData,
  updatedEarlyLoanChart,
} from "../redux/calculations/calculaterSlice";
import arrowImage from "../assets/svg/arrow.svg";
import useLocale from "../../src/locales/index";
import { logEvent } from "firebase/analytics";
import { analytics } from "../firebase";
import i18n from "../i18n";
import DecimalUtils from "../utiles/decimalFormate";
import { Helmet } from "react-helmet";

const EarlyLoanCalc = () => {
  const [result, setResult] = useState(null);
  // const [timeUnit, setTimeUnit] = useState("Year");
  const [showTable, setShowTable] = useState(false);
  const { timeUnit } = useSelector((state) => state.earlyLoanCalculator);
  const cAccept = localStorage.getItem("grow-cookies");
  const cookieAccept = JSON.parse(cAccept);
  const dispatch = useDispatch();
  const { loanAmount, intrestRate, timePeriod, prePayment } = useSelector(
    (state) => state.earlyLoanCalculator
  );
  const [calcDone, setCalcDone] = useState(false);

  const { t, textData, timeUnits } = useLocale();
  const { earlyPayoffCalc } = textData;
  const [isFormModified, setIsFormModified] = useState(false);

  const formatToINR = (value) => {
    if (value === "" || value === null || value === undefined) return "";
    if (value === 0) return "0";

    const shouldForceIndianFormat = i18n.language === "fa";

    return isNaN(Number(value))
      ? ""
      : Number(value).toLocaleString(
          shouldForceIndianFormat ? "en-IN" : i18n.language || "en-IN"
        );
  };

  const calculateEMI = (principal, rate, time) => {
    const monthlyRate = rate / 12 / 100;
    const months = timeUnit === "Year" ? time * 12 : time;
    return (
      (principal * monthlyRate * Math.pow(1 + monthlyRate, months)) /
      (Math.pow(1 + monthlyRate, months) - 1)
    );
  };

  const calculateLoanDetails = (principal, rate, time, emi) => {
    const monthlyRate = rate / 12 / 100;
    const months = timeUnit === "Year" ? time * 12 : time;
    let balance = principal;
    let totalInterest = 0;
    let monthlyPrincipal = 0;
    let monthlyInterest = 0;

    for (let i = 0; i < months; i++) {
      monthlyInterest = balance * monthlyRate;
      monthlyPrincipal = emi - monthlyInterest;
      totalInterest += monthlyInterest;
      balance -= monthlyPrincipal;

      if (balance <= 0) {
        break;
      }
    }

    return {
      totalInterest,
      finalBalance: balance <= 0 ? 0 : balance,
    };
  };

  const validationSchema = useMemo(
    () =>
      Yup.object({
        loanAmount: Yup.number()
          .min(100000, earlyPayoffCalc.loanAmountMin)
          .max(100000000, earlyPayoffCalc.loanAmountMax)
          .required(earlyPayoffCalc.loanAmountRequired),
        intrestRate: Yup.number()
          .min(5, earlyPayoffCalc.interestRateMin)
          .max(15, earlyPayoffCalc.interestRateMax)
          .required(earlyPayoffCalc.interestRateRequired),
        timePeriod: Yup.number()
          .min(
            timeUnit === "Year" ? 1 : 1,
            timeUnit === "Year"
              ? earlyPayoffCalc.timePeriodMinYear
              : earlyPayoffCalc.timePeriodMinMonth
          )
          .max(
            timeUnit === "Year" ? 20 : 240,
            timeUnit === "Year"
              ? earlyPayoffCalc.timePeriodMaxYear
              : earlyPayoffCalc.timePeriodMaxMonth
          )
          .required(earlyPayoffCalc.timePeriodRequired),
        prePayment: Yup.number()
          .min(0, earlyPayoffCalc.prepaymentMin)
          .max(5000000, earlyPayoffCalc.prepaymentMax)
          .required(earlyPayoffCalc.prepaymentRequired)
          .test(
            "prepayment-less-than-loanAmount",
            earlyPayoffCalc.prepaymentLessThanLoanAmount,
            function (value) {
              const { loanAmount } = this.parent;
              return value < loanAmount;
            }
          ),
      }),
    [timeUnit]
  );

  const formik = useFormik({
    initialValues: {
      loanAmount,
      intrestRate,
      timePeriod,
      prePayment,
    },
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,

    onSubmit: (values) => {
      if (cookieAccept && analytics) {
        logEvent(analytics, "calculate_EARLYLOAN");
      }
      const { loanAmount, intrestRate, timePeriod, prePayment } = values;

      const months = timeUnit === "Year" ? timePeriod * 12 : timePeriod;

      const originalEMI = calculateEMI(loanAmount, intrestRate, timePeriod);

      const originalLoanDetails = calculateLoanDetails(
        loanAmount,
        intrestRate,
        timePeriod,
        originalEMI
      );
      const originalTotalInterestRate =
        (originalLoanDetails.totalInterest / loanAmount) * 100;

      const newPrincipal = loanAmount - prePayment;

      const newEMI = calculateEMI(newPrincipal, intrestRate, timePeriod);

      const newLoanDetails = calculateLoanDetails(
        newPrincipal,
        intrestRate,
        timePeriod,
        newEMI
      );
      const newTotalInterestRate =
        (newLoanDetails.totalInterest / newPrincipal) * 100;

      const interestSavings =
        originalLoanDetails.totalInterest - newLoanDetails.totalInterest;

      let monthsSaved = 0;
      if (newPrincipal > 0) {
        const emiWithOriginalPayment = originalEMI;
        let balance = newPrincipal;
        const monthlyRate = intrestRate / 12 / 100;
        let tempMonths = 0;

        while (balance > 0 && tempMonths < months) {
          const interest = balance * monthlyRate;
          const principalPayment = emiWithOriginalPayment - interest;
          balance -= principalPayment;
          tempMonths++;
        }

        monthsSaved = months - tempMonths;
      }

      dispatch(setNewEmi(newEMI));
      dispatch(setTotalAmount(newLoanDetails.totalInterest));
      dispatch(setRemainingPrinciple(newPrincipal));
      dispatch(
        updatedEarlyLoanChart({
          originalEMI,
          newEMI,
          totalInterest: newLoanDetails.totalInterest,
          monthsSaved,
          prePaymentAmount: prePayment,
          loanAmount: loanAmount,
        })
      );

      setResult({
        originalLoanAmount: loanAmount,
        newPrincipal,
        originalEMI,
        newEMI,
        interestSavings,
        monthsSaved,
        prePaymentAmount: prePayment,
        totalInterestWithoutPrepayment: originalLoanDetails.totalInterest,
        totalInterestWithPrepayment: newLoanDetails.totalInterest,
      });

      saveToHistory({
        months: months,
        originalEMI: originalEMI,
        newPrincipal: newPrincipal,
        newEMI: newEMI,
        originalTotalInterestRate: originalTotalInterestRate,
        newTotalInterestRate: newTotalInterestRate,
        interestSavings: interestSavings,
        tabName: "earlyLoanCalc",
        totalInvestment: loanAmount,
        yearlyReturn: interestSavings,
        interestRate: values.intrestRate,
        tenure: timePeriod,
        maturityAmount: newLoanDetails.totalInterest,
        prePaymentAmount: prePayment,
        calculationType: "Early Loan",
      });
      // setShowTable(true);
      setShowTable(false);
      setCalcDone(true);
      setIsFormModified(false);
    },
  });

  useEffect(() => {
    return () => {
      dispatch(resetChartData());
      dispatch(resetEarlyData());
    };
  }, [dispatch]);

  const saveToHistory = (calculationData) => {
    try {
      const existingData =
        JSON.parse(localStorage.getItem(textData.storeData)) || [];

      const newHistoryRecord = {
        tabName: calculationData.tabName,
        prePaymentAmount: calculationData.prePaymentAmount,
        totalInvestment: calculationData.totalInvestment,
        yearlyReturn: 0,
        interestRate: calculationData.interestRate,
        tenure: calculationData.tenure,
        maturityAmount: calculationData.maturityAmount,
        calculationType: calculationData.calculationType,
        months: calculationData.months,
        originalEMI: calculationData.originalEMI,
        newPrincipal: calculationData.newPrincipal,
        originalTotalInterestRate: calculationData.originalTotalInterestRate,
        newTotalInterestRate: calculationData.newTotalInterestRate,
        interestSavings: calculationData.interestSavings,
        calculationDate: new Date().toISOString(),
      };

      const isDuplicate = existingData.some((recordArray) => {
        if (!recordArray || !recordArray[0]) return false;
        const record = recordArray[0];

        return (
          record.calculationType === newHistoryRecord.calculationType &&
          record.totalInvestment === newHistoryRecord.totalInvestment &&
          record.interestRate === newHistoryRecord.interestRate &&
          record.tenure === newHistoryRecord.tenure &&
          record.maturityAmount === newHistoryRecord.maturityAmount &&
          record.prePaymentAmount === newHistoryRecord.prePaymentAmount
        );
      });
      if (!isDuplicate) {
        existingData.push([newHistoryRecord]);
        localStorage.setItem(textData.storeData, JSON.stringify(existingData));
      } else {
      }
    } catch (error) {}
  };

  const [timeUnitValue, setTimeUnitValue] = useState({
    displayValue: formik?.values?.timePeriod,
    years: formik?.values?.timePeriod,
  });

  const sliderTrackRef = useRef(null);
  const interestSliderRef = useRef(null);
  const periodSliderRef = useRef(null);
  const prePaymentSliderRef = useRef(null);

  const handleTimePeriodSliderChange = (e) => {
    const value = parseInt(e.target.value, 10);
    dispatch(setTimePeriod(value));
    formik.setFieldValue("timePeriod", value);
    setIsFormModified(true);
    setCalcDone(false);
  };

  const handleIntrestRateSliderChange = (e) => {
    const newDisplayValue = parseInt(e.target.value, 10);
    let newYearsValue;

    switch (timeUnit) {
      case "Year":
        newYearsValue = newDisplayValue;
        break;
      case "Month":
        newYearsValue = newDisplayValue / 12;
        break;
      default:
        newYearsValue = newDisplayValue;
    }
    formik.setFieldValue("intrestRate", newDisplayValue);
    setTimeUnitValue({
      displayValue: newDisplayValue,
      years: newYearsValue,
    });
    dispatch(setIntrestRate(newDisplayValue));
    setIsFormModified(true);
    setCalcDone(false);
  };

  const handleLoanAmountSliderChange = (e) => {
    const value = Number(e?.target?.value);
    dispatch(setLoanAmmount(value));
    formik.setFieldValue("loanAmount", value);
    setIsFormModified(true);
    setCalcDone(false);
  };

  const handlePrePaymentSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setPrePayment(value));
    formik.setFieldValue("prePayment", value);
    setIsFormModified(true);
    setCalcDone(false);
  };

  const handleTimeUnitToggle = () => {
    const currentDisplayValue = formik.values.timePeriod;
    let newDisplayValue, newYearsValue;

    formik.setFieldError("timePeriod", "");
    if (timeUnit === "Year") {
      dispatch(setTimeUnit("Month"));
      newDisplayValue = currentDisplayValue * 12; // Convert years to months
      newYearsValue = currentDisplayValue;
    } else {
      dispatch(setTimeUnit("Year"));
      newDisplayValue = Math.max(1, Math.round(currentDisplayValue / 12)); // Convert months to years, minimum 1
      newYearsValue = newDisplayValue;
    }
    formik.setFieldValue("timePeriod", newDisplayValue);
    setTimeUnitValue({
      displayValue: newDisplayValue,
      years: newYearsValue,
    });

    localStorage.setItem(
      "loanTimeUnit",
      timeUnit === "Year" ? "Month" : "Year"
    );
    setIsFormModified(true);
    setCalcDone(false);
  };

  const getSliderMax = () => {
    switch (timeUnit) {
      case "Year":
        return 20;
      case "Month":
        return 540; // 45 years * 12

      default:
        return 20;
    }
  };

  const getSliderMin = () => {
    switch (timeUnit) {
      case "Year":
        return 1;
      case "Month":
        return 1;

      default:
        return 1;
    }
  };

  const getSliderPosition = () => {
    const min = getSliderMin();
    const max = getSliderMax();
    return ((formik?.values?.timePeriod - min) / (max - min)) * 100;
  };

  const getInputLabel = () => {
    switch (timeUnit) {
      case "Year":
        return t("yearShort");
      case "Month":
        return t("month").charAt(0).toUpperCase();
      default:
        return "Yr";
    }
  };

  useEffect(() => {
    dispatch(setTimePeriod(formik?.values?.timePeriod));
    dispatch(setIntrestRate(formik?.values?.intrestRate));
    dispatch(setPrePayment(formik?.values?.prePayment));
    dispatch(setTimeUnit(timeUnit));
  }, [formik.values, dispatch]);

  const handleLoanChange = (e) => {
    const inputValue = e.target.value;
    if (inputValue === "") {
      formik.setFieldValue("loanAmount", "");
      return;
    }
    const rawValue = inputValue.replace(/[^0-9]/g, "");
    if (rawValue.length > 9) return;

    if (rawValue === "0") {
      formik.setFieldValue("loanAmount", 0);
      return;
    }
    formik.setFieldValue("loanAmount", rawValue ? Number(rawValue) : "");
    setIsFormModified(true);
    setCalcDone(false);
  };

  const handlePrePayChange = (e) => {
    const inputValue = e.target.value;
    if (inputValue === "") {
      formik.setFieldValue("prePayment", "");
      return;
    }
    const rawValue = inputValue.replace(/[^0-9]/g, "");
    if (rawValue.length > 9) return;

    if (rawValue === "0") {
      formik.setFieldValue("prePayment", 0);
      return;
    }
    formik.setFieldValue("prePayment", rawValue ? Number(rawValue) : "");
    setIsFormModified(true);
    setCalcDone(false);
  };
  const handleInterestRateChange = (e) => {
    const rawValue = e.target.value;
    if (rawValue === "") {
      formik.setFieldValue("intrestRate", rawValue);
      dispatch(setIntrestRate(""));
      return;
    }
    if (rawValue.length > 5) return;
    if (!DecimalUtils.getValidationPattern().test(rawValue)) return;

    const normalizedValue = DecimalUtils.normalizeInput(rawValue);
    formik.setFieldValue("intrestRate", normalizedValue);
    dispatch(setIntrestRate(normalizedValue));
    setIsFormModified(true);
    setCalcDone(false);
  };
  const handleTimeChange = (e) => {
    const rawValue = e.target.value;
    if (rawValue.length > 2) return;
    formik.setFieldValue("timePeriod", rawValue);
    if (rawValue === "") return;
    const numericValue = Number(rawValue);
    if (!isNaN(numericValue)) {
      const clamped = Math.max(1, Math.min(30, numericValue));
      dispatch(setTimePeriod(clamped));
      setIsFormModified(true);
    }
    setCalcDone(false);
  };
  const isRTL = () => {
    const rtlLanguages = ["ar", "fa", "ur"];
    return rtlLanguages.includes(i18n.language);
  };

  const descriptionContent = (
    <div className="p-4 sm:p-5 w-full bg-[#F6F8FB] rounded-xl">
      <h2 className="text-black text-base sm:text-xl font-medium">
        {" "}
        {textData.earlyContent.title}
      </h2>
      <p className="leading-relaxed mt-2  text-sm sm:text-[15px]">
        {textData.earlyContent.description}{" "}
      </p>

      <h2 className="text-black text-base sm:text-xl font-medium mt-5">
        {" "}
        {textData.earlyContent.howItWorksTitle}
      </h2>

      <p className="leading-relaxed mt-2  text-sm sm:text-[15px]">
        {textData.earlyContent.howItWorksSteps}
      </p>
      <p className="font-medium bg-[#E0E4F0] text-sm sm:text-base p-3 mt-3">
        <strong>
          {textData.earlyContent.formula} = P × r × (1 + r)<sup>n</sup> / ((1 +
          r)<sup>n</sup> - 1)
        </strong>
      </p>

      <table className="border-collapse mt-4 w-full">
        <tbody>
          <tr>
            <td className="p-3 border border-gray-light w-1/3 font-bold text-sm sm:text-[15px]">
              P
            </td>
            <td className="p-3 border border-gray-light w-2/3 text-sm sm:text-[15px]">
              {textData.earlyContent.inputsTable.P}
            </td>
          </tr>
          <tr className="bg-transparent">
            <td className="p-3 border border-gray-light w-1/3 font-bold text-sm sm:text-[15px]">
              r
            </td>
            <td className="p-3 border border-gray-light w-2/3 text-sm sm:text-[15px]">
              {textData.earlyContent.inputsTable.r}
            </td>
          </tr>
          <tr className="bg-transparent">
            <td className="p-3 border border-gray-light w-1/3 font-bold text-sm sm:text-[15px]">
              n
            </td>
            <td className="p-3 border border-gray-light w-2/3 text-sm sm:text-[15px]">
              {textData.earlyContent.inputsTable.n}
            </td>
          </tr>
        </tbody>
      </table>

      <h2 className="text-black text-base sm:text-xl font-medium mt-8">
        {" "}
        {textData.earlyContent.stepsToUseTitle}
      </h2>
      <ol className="list-decimal px-5 leading-relaxed mt-3 text-sm sm:text-[15px]">
        <li>
          <strong>{textData.earlyContent.outstandingLoan}</strong>
          {textData.earlyContent.stepsToUse[0]}
        </li>
        <li>
          <strong>{textData.earlyContent.loanTenture}</strong>{" "}
          {textData.earlyContent.stepsToUse[0]}
        </li>
        <li>
          <strong>{textData.earlyContent.inpRate}</strong>{" "}
          {textData.earlyContent.stepsToUse[1]}
        </li>
        <li>
          <strong>{textData.earlyContent.prepayment}</strong>{" "}
          {textData.earlyContent.stepsToUse[2]}
        </li>
        <li>
          <strong>{textData.earlyContent.calculate}</strong>{" "}
          {textData.earlyContent.stepsToUse[3]}
        </li>
      </ol>

      <h2 className="text-black text-base sm:text-xl font-medium mt-8">
        {" "}
        {textData.earlyContent.benefitsTitle}
      </h2>
      <ul className="list-disc px-5 leading-relaxed mt-3 text-sm sm:text-[15px]">
        <li>
          <strong>{textData.earlyContent.saveIntrest}</strong>{" "}
          {textData.earlyContent.benefitsList[0]}
        </li>
        <li>
          <strong>{textData.earlyContent.reduceTenure}</strong>{" "}
          {textData.earlyContent.benefitsList[1]}
        </li>
        <li>
          <strong>{textData.earlyContent.flexPlan}</strong>{" "}
          {textData.earlyContent.benefitsList[2]}
        </li>
        <li>
          <strong>{textData.earlyContent.InstantResults}</strong>{" "}
          {textData.earlyContent.benefitsList[3]}
        </li>
        <li>
          <strong>{textData.earlyContent.InformedDecisions}</strong>{" "}
          {textData.earlyContent.benefitsList[4]}
        </li>
      </ul>

      <h2 className="text-black text-base sm:text-xl font-medium mt-8">
        {" "}
        {textData.earlyContent.whenToUseTitle}
      </h2>
      <p className="leading-relaxed mt-2  text-sm sm:text-[15px]">
        {textData.earlyContent.scenario}
      </p>
      <ul className="list-disc px-5 leading-relaxed mt-3 text-sm sm:text-[15px]">
        <li>
          <strong>{textData.earlyContent.earlyYear}</strong>{" "}
          {textData.earlyContent.whenToUseList[0]}
        </li>
        <li>
          <strong>{textData.earlyContent.afterWindfall}</strong>{" "}
          {textData.earlyContent.whenToUseList[1]}
        </li>
        <li>
          <strong>{textData.earlyContent.complareOpt}</strong>
          {textData.earlyContent.whenToUseList[2]}
        </li>
        <li>
          <strong>{textData.earlyContent.RegularReviews}</strong>
          {textData.earlyContent.whenToUseList[3]}
        </li>
      </ul>

      <h2 className="text-black text-base sm:text-xl font-medium mt-8">
        {" "}
        {textData.earlyContent.considerationsTitle}
      </h2>
      <ul className="list-disc px-5 leading-relaxed mt-3 text-sm sm:text-[15px]">
        <li>
          <strong>{textData.earlyContent.futureNeed}</strong>{" "}
          {textData.earlyContent.considerationsList[0]}
        </li>
        <li>
          <strong>{textData.earlyContent.taxBenifits}</strong>
          {textData.earlyContent.considerationsList[1]}
        </li>
        <li>
          <strong>{textData.earlyContent.investAlt}</strong>{" "}
          {textData.earlyContent.considerationsList[2]}
        </li>
        <li>
          <strong>{textData.earlyContent.emiTnture}</strong>{" "}
          {textData.earlyContent.considerationsList[3]}
        </li>
      </ul>
    </div>
  );
  const getPlaceholderValue = (language) => {
    const baseValue = 10000;
    const formatLang = language === "fa" ? "en-IN" : language;
    return new Intl.NumberFormat(formatLang).format(baseValue);
  };
  const getPlaceholderPayValue = (language) => {
    const baseValue = 5000;
    return new Intl.NumberFormat(language).format(baseValue);
  };

  return (
    <>
      <Helmet>
        <title>Early Loan Payoff Calculator | Become Debt-Free Faster</title>
        <meta
          name="description"
          content="Early Loan Payoff Calculator by GrowLifeFund helps you pay loans faster, reduce debt faster, save on interest, and achieve debt-free financial freedom."
        />
        <link
          rel="canonical"
          href="https://growlifefund.com/calculator/loan-payoff-calculator"
        />

        <meta
          property="og:title"
          content="Early Loan Payoff Calculator | Become Debt-Free Faster"
        />
        <meta
          property="og:description"
          content="Early Loan Payoff Calculator by GrowLifeFund helps you pay loans faster, reduce debt faster, save on interest, and achieve debt-free financial freedom."
        />
        <meta
          property="og:url"
          content="https://growlifefund.com/calculator/loan-payoff-calculator"
        />
        <meta property="og:site_name" content="growlifefund" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en" />
        <meta
          property="og:image:alt"
          content="GrowLifeFund Early Loan Payoff Calculator"
        />

        <meta name="robots" content="index, follow" />
      </Helmet>

      <CalculatorLayout
        generateData={result}
        title={
          <>
            <span className="text-black lg:text-xl sm:text-lg text-lg">
              {textData.your}{" "}
            </span>
            {textData.earlyPayoffCalc.titleMain}{" "}
            <span className="text-black lg:text-xl sm:text-lg text-lg">
              {textData.earlyPayoffCalc.titleSuffix}
            </span>
          </>
        }
        description={textData.earlyPayoffCalc.description}
        descriptionContent={descriptionContent}
        setShowTable={setShowTable}
        showTable={showTable}
        calcDone={calcDone}
        isFormModified={isFormModified}
        setIsFormModified={setIsFormModified}
      >
        <div className="">
          <form onSubmit={formik.handleSubmit} noValidate className="flex flex-col gap-6">
            <div>
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="font-normal text-start  text-black sm:text-sm text-sm">
                  {textData.earlyPayoffCalc.outstandingAmountLabel}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div className="relative w-full " ref={sliderTrackRef}>
                  {/* Background track */}
                  
                  <input
                    type="range"
                    min={100000}
                    max={100000000}
                    step={1000}
                    value={formik?.values?.loanAmount || 0}
                    onChange={handleLoanAmountSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${
                        ((formik?.values?.loanAmount - 100000) /
                          (100000000 - 100000)) *
                        100
                      }%`,
                    }}
                  />
                </div>{" "}
                <div className="flex items-end flex-col">
                  <Input
                    name="loanAmount"
                    leftIcon={textData.currency}
                    value={formatToINR(formik?.values?.loanAmount)}
                    onChange={handleLoanChange}
                    onBlur={formik.handleBlur}
                    type="text"
                    dir="ltr"
                    placeholder={getPlaceholderValue(i18n.language)}
                    borderColor={
                      formik.touched.loanAmount && formik.errors.loanAmount
                        ? "border-red-500"
                        : "border-gray-light"
                    }
                    className={`sm:h-[36px] h-[32px]   ${
                      formik.touched.loanAmount && formik.errors.loanAmount
                        ? "border-danger"
                        : "border-gray-light"
                    } sm:h-[36px] h-[32px] flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px] `}
                  />
                  {formik.touched.loanAmount && formik.errors.loanAmount && (
                    <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                      {formik.errors.loanAmount}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="font-normal text-start text-black sm:text-sm text-sm">
                  {textData.rateOfInterest}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div className="relative w-full " ref={interestSliderRef}>
                  {/* Background track */}
                  
                  <input
                    type="range"
                    min={5}
                    max={15}
                    step={0.1}
                    value={parseFloat(formik?.values?.intrestRate)}
                    onChange={handleIntrestRateSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${
                        ((formik?.values?.intrestRate - 5) / (15 - 5)) * 100
                      }%`,
                    }}
                  />
                </div>
                <div className="flex items-end flex-col">
                  <Input
                    name="intrestRate"
                    leftIcon="%"
                    type="text"
                    placeholder={DecimalUtils.getPlaceholder("12.0")}
                    value={DecimalUtils.formatDisplay(
                      formik.values.intrestRate
                    )}
                    onChange={handleInterestRateChange}
                    onBlur={formik.handleBlur}
                    borderColor={
                      formik?.touched?.intrestRate &&
                      formik?.errors?.intrestRate
                        ? "border-red-500"
                        : "border-gray-light"
                    }
                    className={` sm:h-[36px] h-[32px]   ${
                      formik.touched.intrestRate && formik.errors.intrestRate
                        ? "border-danger"
                        : "border-gray-light"
                    } sm:h-[36px] h-[32px] flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px] `}
                  />
                  {formik?.touched?.intrestRate &&
                    formik?.errors?.intrestRate && (
                      <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                        {formik.errors.intrestRate}
                      </div>
                    )}
                </div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2 gap-4">
                <div className="flex  flex-row items-start gap-1">
                  <label className="text-start font-normal text-black sm:text-sm text-sm">
                    {textData.timePeriod}
                  </label>
                  <div
                    className="ml-0 sm:ml-2 flex items-center cursor-pointer"
                    onClick={handleTimeUnitToggle}
                  >
                    <div className="relative flex items-center gap-[2px]">
                      <div className="text-primary sm:text-sm text-sm font-normal">
                        {timeUnits[timeUnit]}
                      </div>
                      <div className="absolute -bottom-1 left-0 right-0 h-0.5 "></div>
                      <img
                        src={arrowImage}
                        alt="Dropdown arrow"
                        className="w-3 h-3 sm:w-4 sm:h-4"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div className="relative w-full" ref={periodSliderRef}>
                  {/* Background track */}
                  
                  <input
                    type="range"
                    min={getSliderMin()}
                    max={getSliderMax()}
                    step={1}
                    value={formik?.values?.timePeriod}
                    onChange={handleTimePeriodSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${getSliderPosition()}%`,
                    }}
                  />
                </div>{" "}
                <div className="flex items-end flex-col">
                  <Input
                    name="timePeriod"
                    leftIcon={getInputLabel()}
                    value={formik?.values?.timePeriod}
                    onChange={handleTimeChange}
                    onBlur={formik.handleBlur}
                    type="number"
                    placeholder={"5"}
                    borderColor={
                      formik?.touched?.timePeriod && formik?.errors?.timePeriod
                        ? "border-red-500"
                        : "border-gray-light"
                    }
                    className={` sm:h-[36px] h-[32px]   ${
                      formik.touched.timePeriod && formik.errors.timePeriod
                        ? "border-danger"
                        : "border-gray-light"
                    } sm:h-[36px] h-[32px] flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px] `}
                  />
                  {formik?.touched?.timePeriod &&
                    formik?.errors?.timePeriod && (
                      <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                        {formik.errors.timePeriod}
                      </div>
                    )}
                </div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="font-normal text-start text-black sm:text-sm text-sm">
                  {textData.earlyPayoffCalc.prepaymentAmountLabel}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div className="relative w-full " ref={prePaymentSliderRef}>
                  {/* Background track */}
                  
                  <input
                    type="range"
                    min={0}
                    max={5000000}
                    step={1000}
                    value={formik?.values?.prePayment}
                    onChange={handlePrePaymentSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${
                        ((formik?.values?.prePayment - 0) / (5000000 - 0)) * 100
                      }%`,
                    }}
                  />
                </div>
                <div className="flex items-end flex-col">
                  <Input
                    name="prePayment"
                    leftIcon={textData.currency}
                    value={formatToINR(formik?.values?.prePayment)}
                    onChange={handlePrePayChange}
                    onBlur={formik.handleBlur}
                    type="text"
                    dir="ltr"
                    placeholder={getPlaceholderPayValue(i18n.language)}
                    borderColor={
                      formik?.touched?.prePayment && formik?.errors?.prePayment
                        ? "border-red-500"
                        : "border-gray-light"
                    }
                    className={`sm:h-[36px] h-[32px]   ${
                      formik.touched.prePayment && formik.errors.prePayment
                        ? "border-danger"
                        : "border-gray-light"
                    } sm:h-[36px] h-[32px] flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px] `}
                  />
                  {formik.touched.prePayment && formik.errors.prePayment && (
                    <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                      {formik.errors.prePayment}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-[10px]">
              <Button type="submit" className="w-full">
                {textData.calculate}
              </Button>
            </div>
          </form>
        </div>
      </CalculatorLayout>
    </>
  );
};

export default EarlyLoanCalc;
